import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useGoogleAuthPopup } from 'src/hook/useGoogleAuthPopup';
import { useMicroSoftAuthPopup } from 'src/hook/useMicroSoftAuthPopup';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { useChannelContext } from '../../hooks';

export function EmailStep3() {
  const { onChangeStep, state: channelState, handleEmailChannelDtls } = useChannelContext();
  const { channelName, email } = channelState.emailChannelDtls || { channelName: '', email: '' };

  const { loginWithMicrosoft, isMSAccVerified } = useMicroSoftAuthPopup();
  const { loginWithGoogle, isGoogleAccVerified } = useGoogleAuthPopup();

  const { t } = useTranslate('translation');

  const emailProviderType = channelState.emailProviderType;

  type CampaignField = { channelName: string; email: string };

  const fldList = [
    { name: 'channelName', required: true },
    { name: 'email', required: emailProviderType === 'other_provider' },
  ];

  const CampaignSchema = validationSchema(fldList, t);

  const defaultValues: CampaignField = {
    channelName: '',
    email: '',
  };

  const methods = useForm<CampaignField>({
    mode: 'all',
    resolver: zodResolver(CampaignSchema),
    defaultValues,
    values: { channelName, email },
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = methods;

  const [watchedChannelName, watchedEmail] = watch(['channelName', 'email']);

  const onSubmit = handleSubmit(async (data) => {
    handleEmailChannelDtls(data);
    reset();
  });

  const signInBtnHandler = () => {
    const channelValue = getValues('channelName');
    handleEmailChannelDtls({ ...channelState.emailChannelDtls, channelName: channelValue });
    if (emailProviderType === 'google') {
      loginWithGoogle();
    } else if (emailProviderType === 'microsoft') {
      loginWithMicrosoft();
    }
  };

  const isNextDisabled = () => {
    if (emailProviderType === 'other_provider') {
      return !watchedChannelName || !watchedEmail || !!errors.email;
    }

    return !watchedChannelName || (!isGoogleAccVerified && !isMSAccVerified);
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} style={{ height: '100%' }}>
      <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Grid container spacing={3} justifyContent="space-between">
            <Field.Text
              name="channelName"
              label={t('CHANNEL_NAME')}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder={t('CHANNEL_NAME')}
            />

            {emailProviderType === 'other_provider' ? (
              <Field.Text
                name="email"
                label={t('EMAIL')}
                slotProps={{ inputLabel: { shrink: true } }}
                placeholder={t('EMAIL')}
              />
            ) : (
              <Box sx={{ my: 2, width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{ background: '#50a28a' }}
                  onClick={signInBtnHandler}
                  disabled={!watchedChannelName}
                >
                  {t('SIGN_IN_WITH')} {t(emailProviderType.toUpperCase())}
                </Button>

                {(isGoogleAccVerified || isMSAccVerified) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                    <Iconify icon="eva:checkmark-fill" sx={{ color: 'primary.main' }} />
                    <Typography sx={{ color: '#50a28a' }}>{t('ACCOUNT_VERIFIED')}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box>
            <Button variant="outlined" color="inherit" onClick={() => onChangeStep('back')}>
              {t('PREVIOUS')}
            </Button>
          </Box>
          <Box>
            <Button
              disabled={isNextDisabled()}
              size="small"
              variant="contained"
              type="submit"
              onClick={() => onChangeStep('next')}
            >
              {t('NEXT')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Form>
  );
}
